import { Plus, Minus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useCart } from '@/context/CartContext';

const FALLBACK_IMG = 'https://images.unsplash.com/photo-1542838132-92c53300491e?w=400';

export function ProductCard({ product }) {
  const { addToCart, updateQuantity, getItemQuantity } = useCart();
  const quantity = getItemQuantity(product.id);
  const hasDiscount = product.original_price && product.original_price > product.price;
  const discountPercent = hasDiscount
    ? Math.round((1 - product.price / product.original_price) * 100)
    : 0;

  return (
    <div
      className="bg-white border border-border rounded-xl overflow-hidden flex flex-col h-full product-card shadow-sm"
      data-testid={`product-card-${product.id}`}
    >
      {/* Image */}
      <div className="relative img-container bg-muted/30">
        <img
          src={product.image_url}
          alt={product.name}
          className="w-full h-full object-cover"
          loading="lazy"
          decoding="async"
          onError={(e) => { if (e.target.src !== FALLBACK_IMG) e.target.src = FALLBACK_IMG; }}
        />
        {hasDiscount && (
          <span className="absolute top-2 left-2 bg-accent text-accent-foreground text-xs font-bold px-2 py-1 rounded-full shadow">
            {discountPercent}% OFF
          </span>
        )}
        {!product.in_stock && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
            <span className="text-white font-semibold">Out of Stock</span>
          </div>
        )}
      </div>

      {/* Details */}
      <div className="p-3 flex flex-col flex-1">
        <p className="text-xs text-muted-foreground mb-1 font-medium">{product.unit}</p>
        <h3 className="font-semibold text-base line-clamp-2 leading-snug" data-testid={`product-name-${product.id}`}>
          {product.name}
        </h3>
        {product.telugu_name && (
          <p className="text-sm text-muted-foreground mt-0.5 line-clamp-1" data-testid={`product-telugu-${product.id}`}>
            {product.telugu_name}
          </p>
        )}

        {/* Price */}
        <div className="flex items-center gap-2 mt-2 mb-3">
          <span className="font-extrabold text-primary text-lg">₹{product.price}</span>
          {hasDiscount && (
            <span className="text-xs text-muted-foreground line-through">
              ₹{product.original_price}
            </span>
          )}
        </div>

        {/* Add to Cart */}
        {product.in_stock && (
          quantity === 0 ? (
            <Button
              onClick={() => addToCart(product)}
              className="w-full h-12 rounded-full bg-primary hover:bg-primary/90 text-base font-bold add-to-cart-btn shadow-sm"
              data-testid={`add-to-cart-${product.id}`}
            >
              <Plus className="w-5 h-5 mr-1" />
              ADD
            </Button>
          ) : (
            <div className="flex items-center justify-between bg-primary text-white rounded-full h-12 px-1">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => updateQuantity(product.id, quantity - 1)}
                className="h-10 w-10 rounded-full qty-btn text-white hover:bg-white/20 hover:text-white"
                data-testid={`decrease-qty-${product.id}`}
              >
                <Minus className="w-5 h-5" />
              </Button>
              <span className="font-extrabold text-lg" data-testid={`qty-${product.id}`}>
                {quantity}
              </span>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => updateQuantity(product.id, quantity + 1)}
                className="h-10 w-10 rounded-full qty-btn text-white hover:bg-white/20 hover:text-white"
                data-testid={`increase-qty-${product.id}`}
              >
                <Plus className="w-5 h-5" />
              </Button>
            </div>
          )
        )}
      </div>
    </div>
  );
}
